import type { SetupGuide } from './SetupGuidePanel';

/** Marketing WhatsApp / SMS / Email — keyed by channel + provider. */
export function getMarketingSetupGuide(
  channel: 'WhatsApp' | 'SMS' | 'Email',
  provider: string
): SetupGuide {
  if (channel === 'WhatsApp' && provider === 'meta_cloud') {
    return {
      title: 'Setup guide — Meta WhatsApp Cloud API',
      intro:
        'Use Meta Cloud API for outbound marketing messages and order updates. This is separate from WhatsApp Shop (catalogue) under Marketplaces.',
      pathHint: 'Meta Business Suite → WhatsApp Manager → API Setup  (or developers.facebook.com → your app → WhatsApp)',
      steps: [
        {
          title: 'Create or open a Meta app',
          body: 'In Meta for Developers, create a Business type app and add the WhatsApp product.',
        },
        {
          title: 'Add a WhatsApp Business phone number',
          body: 'Complete Business verification if prompted, then add/register the phone number you will send from.',
        },
        {
          title: 'Copy Phone Number ID',
          body: 'From WhatsApp → API Setup, copy the Phone number ID into SaleDesk.',
        },
        {
          title: 'Generate a permanent access token',
          body: 'Create a System User in Business Manager, assign WhatsApp permissions, generate a token, and paste it here.',
          note: 'Short-lived User tokens expire — prefer a permanent System User token for production.',
        },
        {
          title: 'Save and send a test',
          body: 'Save credentials, enter your mobile as test recipient, then Send test. Status should become Connected.',
        },
      ],
      tips: [
        'Marketing WhatsApp does not use WhatsApp Shop tokens — configure them separately.',
        'Order notifications and campaigns only message customers with a valid mobile on file.',
        'Promotional broadcasts may need customer consent (UK PECR/GDPR).',
      ],
    };
  }

  if (channel === 'WhatsApp' && provider === 'twilio_whatsapp') {
    return {
      title: 'Setup guide — Twilio WhatsApp',
      intro: 'Send WhatsApp messages through your Twilio account (sandbox for testing, approved sender for live).',
      pathHint: 'Twilio Console → Messaging → Try it out → Send a WhatsApp message  (or Messaging → Senders)',
      steps: [
        {
          title: 'Open your Twilio account',
          body: 'Sign in at twilio.com. Note your Account SID and Auth Token from the Console dashboard.',
        },
        {
          title: 'Enable WhatsApp',
          body: 'Join the WhatsApp Sandbox for testing, or request a WhatsApp sender for production.',
        },
        {
          title: 'Copy credentials into SaleDesk',
          bullets: [
            'Account SID → API key field',
            'Auth Token → API secret field',
            'From → whatsapp:+44… (sandbox or approved number)',
          ],
        },
        {
          title: 'Save and Send test',
          body: 'Use a mobile that is joined to the sandbox (if testing). Confirm Status becomes Connected.',
        },
      ],
      tips: [
        'Sandbox only delivers to numbers that replied with the join code.',
        'Keep Auth Token secret — never paste it into Support chat.',
      ],
    };
  }

  if (channel === 'SMS' && provider === 'twilio_sms') {
    return {
      title: 'Setup guide — Twilio SMS',
      intro: 'UK/EU SMS via Twilio for order alerts and campaigns.',
      pathHint: 'Twilio Console → Phone Numbers → Manage → Active numbers',
      steps: [
        {
          title: 'Buy or select an SMS-capable number',
          body: 'Choose a number that can send SMS to your customers’ country (e.g. UK).',
        },
        {
          title: 'Copy Account SID and Auth Token',
          body: 'From the Twilio Console home page.',
        },
        {
          title: 'Paste into SaleDesk',
          bullets: [
            'Account SID → API key',
            'Auth Token → API secret',
            'From → your Twilio number in E.164 (+44…)',
          ],
        },
        {
          title: 'Save and Send test',
          body: 'Send a test to your own mobile, then enable the channel under Order notifications / Campaigns.',
        },
      ],
      tips: ['Only customers with a valid mobile are messaged.', 'Failed sends appear in Message usage.'],
    };
  }

  if (channel === 'SMS' && provider === 'vonage') {
    return {
      title: 'Setup guide — Vonage (Nexmo) SMS',
      pathHint: 'Vonage Dashboard → API settings → API key & secret',
      steps: [
        {
          title: 'Create a Vonage account',
          body: 'Sign up at vonage.com and top up credit if needed.',
        },
        {
          title: 'Copy API key and API secret',
          body: 'From the dashboard API settings page.',
        },
        {
          title: 'Set From / Sender',
          body: 'Use an approved sender ID or virtual number, then paste credentials into SaleDesk.',
        },
        {
          title: 'Save and Send test',
          body: 'Confirm delivery to your handset before enabling campaigns.',
        },
      ],
    };
  }

  if (channel === 'SMS' && provider === 'firetext') {
    return {
      title: 'Setup guide — FireText SMS',
      intro: 'UK SMS provider — API key only (no secret).',
      pathHint: 'FireText account → Settings → API',
      steps: [
        {
          title: 'Log in to FireText',
          body: 'Open your FireText account and locate the API key.',
        },
        {
          title: 'Paste into SaleDesk',
          bullets: ['API key → API key field', 'From → your approved sender name or number'],
        },
        {
          title: 'Save and Send test',
          body: 'Check the message arrives, then enable SMS for notifications or campaigns.',
        },
      ],
    };
  }

  if (channel === 'Email' && provider === 'smtp') {
    return {
      title: 'Setup guide — SMTP email',
      intro: 'Send with your own mailbox (Google Workspace, Microsoft 365, cPanel, etc.).',
      pathHint: 'Your email admin → SMTP settings  (Gmail: Google Account → Security → App passwords)',
      steps: [
        {
          title: 'Enable SMTP access',
          body: 'For Gmail/Google Workspace, create an App Password (2FA required). For Microsoft 365, allow SMTP AUTH for the mailbox.',
        },
        {
          title: 'Enter host and port',
          bullets: [
            'Gmail: smtp.gmail.com · port 587 · TLS on',
            'Microsoft 365: smtp.office365.com · port 587 · TLS on',
          ],
        },
        {
          title: 'Enter username and password',
          body: 'Usually your full email address and the app password (not your normal login if 2FA is on).',
        },
        {
          title: 'Set From email / name',
          body: 'From email should match the mailbox you authenticated with (or an allowed alias).',
        },
        {
          title: 'Save and Send test',
          body: 'Send a test to yourself. Status Connected means SaleDesk can deliver order emails and campaigns.',
        },
      ],
      tips: [
        'Do not use personal consumer accounts for high-volume marketing if your provider blocks it.',
        'Promotional email may require consent — transactional order updates are usually fine.',
      ],
    };
  }

  if (channel === 'Email' && provider === 'sendgrid') {
    return {
      title: 'Setup guide — SendGrid',
      pathHint: 'SendGrid → Settings → API Keys',
      steps: [
        {
          title: 'Create an API key',
          body: 'Create a key with Mail Send permission. Copy it once — SendGrid will not show it again.',
        },
        {
          title: 'Verify a sender',
          body: 'Verify Single Sender or authenticate your domain in SendGrid before live sends.',
        },
        {
          title: 'Paste into SaleDesk',
          bullets: ['API key → API key field', 'From email / name → verified sender'],
        },
        {
          title: 'Save and Send test',
          body: 'Confirm the test arrives, then enable Email under Order notifications.',
        },
      ],
    };
  }

  if (channel === 'Email' && provider === 'mailgun') {
    return {
      title: 'Setup guide — Mailgun',
      pathHint: 'Mailgun → Sending → Domain settings · API Security → API keys',
      steps: [
        {
          title: 'Add and verify your domain',
          body: 'Add DNS records Mailgun provides (SPF/DKIM) so mail is not marked as spam.',
        },
        {
          title: 'Copy API key and domain',
          body: 'Paste API key and Mailgun domain (e.g. mg.yourshop.com) into SaleDesk.',
        },
        {
          title: 'Set From email',
          body: 'Use an address on the verified domain.',
        },
        {
          title: 'Save and Send test',
          body: 'Check inbox (and spam) for the test message.',
        },
      ],
    };
  }

  return {
    title: `Setup guide — ${channel}`,
    intro: `Choose a provider above, then follow the steps to connect ${channel}.`,
    steps: [
      {
        title: 'Select a provider',
        body: 'Pick the service you already use (or plan to buy).',
      },
      {
        title: 'Paste credentials',
        body: 'Fill the fields shown for that provider. Secrets stay encrypted on your company record.',
      },
      {
        title: 'Send a test',
        body: 'Use Send test, then enable the channel for Order notifications or Campaigns.',
      },
    ],
  };
}

/** OAuth scopes SaleDesk uses for Inventory / Fulfillment / Account APIs. */
export const EBAY_OAUTH_SCOPES = [
  'https://api.ebay.com/oauth/api_scope',
  'https://api.ebay.com/oauth/api_scope/sell.inventory',
  'https://api.ebay.com/oauth/api_scope/sell.fulfillment',
  'https://api.ebay.com/oauth/api_scope/sell.account',
];

export const EBAY_SETUP_GUIDE: SetupGuide = {
  title: 'How to get credentials from eBay',
  intro:
    'Use the eBay Developers Program and Seller Hub to collect the values SaleDesk needs — tokens, policy IDs, location key and category. Prefer Connect with eBay when it is available; otherwise paste tokens from the Developer portal.',
  pathHint:
    'developer.ebay.com → My Account → Application Keysets  ·  Seller Hub → Account → Business policies',
  steps: [
    {
      title: 'Open the eBay Developers Program',
      body: 'Sign in at developer.ebay.com with the same eBay account you sell with (UK / EBAY_GB). Join the Developers Program if prompted.',
    },
    {
      title: 'Create a Production keyset',
      body: 'Go to My Account → Application Keysets (or developer.ebay.com/my/keys). Click Create a keyset and choose Production for live selling.',
      bullets: [
        'App ID (Client ID)',
        'Cert ID (Client Secret)',
        'Dev ID',
      ],
      note: 'Sandbox keysets do not return real orders. Use Production for SaleDesk live sync.',
    },
    {
      title: 'Configure User Tokens / RuName',
      body: 'Next to your App ID, open User Tokens. Under “Get a Token from eBay via Your Application”, add an eBay Redirect URL, enable OAuth, save, then copy the RuName (it is an ID, not a full URL).',
    },
    {
      title: 'Get Access token & Refresh token',
      body: 'From User Tokens, sign in to Production (or complete the OAuth consent) and copy the user Access token and Refresh token. Paste them into SaleDesk if Connect with eBay is not available.',
      note: 'Never paste tokens into chat or email. Paste only into the SaleDesk eBay form.',
    },
    {
      title: 'Copy business policy IDs',
      body: 'In eBay Seller Hub: Account (or Settings) → Business policies. Open each Payment, Return and Fulfilment/Shipping policy — the Profile ID is the number at the end of the browser URL after “=”.',
      bullets: [
        'Fulfillment / Shipping policy ID → Fulfillment policy ID',
        'Payment policy ID',
        'Return policy ID',
      ],
    },
    {
      title: 'Merchant location key',
      body: 'Use the warehouse/location key from your eBay Inventory locations (Seller Hub inventory settings, or shown after Test connection in SaleDesk). Create a location in eBay first if the list is empty.',
    },
    {
      title: 'Default category ID',
      body: 'Pick the numeric leaf category for your products from an existing UK listing URL, Seller Hub category picker, or the eBay Category Tree / Taxonomy tools on developer.ebay.com.',
    },
  ],
  tips: [
    'Marketplace ID for UK is EBAY_GB.',
    'Keep SKUs identical between SaleDesk and eBay listings.',
    'After pasting values, use Test connection before the first product sync.',
  ],
  warning: 'Never share App Secret (Cert ID), refresh tokens or access tokens outside SaleDesk.',
};

export const WHATSAPP_SHOP_SETUP_GUIDE: SetupGuide = {
  title: 'Setup guide — WhatsApp Shop (catalogue)',
  intro:
    'WhatsApp Shop lists your catalogue and can take inbound orders. It is separate from Marketing WhatsApp (outbound promotions / order SMS-style alerts).',
  pathHint: 'Meta Business Suite → WhatsApp Manager → Catalog  ·  Meta for Developers → your app → WhatsApp',
  steps: [
    {
      title: 'Create a Meta Business + WhatsApp app',
      body: 'Add the WhatsApp product and link your WhatsApp Business Account (WABA).',
    },
    {
      title: 'Create or connect a Commerce catalogue',
      body: 'In Commerce Manager / WhatsApp Manager, create a catalogue and note the Catalog ID.',
    },
    {
      title: 'Copy IDs into SaleDesk',
      bullets: [
        'Phone Number ID',
        'WhatsApp Business Account ID (WABA)',
        'Catalog ID',
        'Business display name',
      ],
    },
    {
      title: 'Access token and App secret',
      body: 'Generate a permanent access token with catalogue permissions. Copy the App Secret from your Meta app settings.',
    },
    {
      title: 'Webhook verify token',
      body: 'Choose any random verify string, save it here, and use the same value when configuring the webhook in Meta.',
    },
    {
      title: 'Point Meta webhook to SaleDesk',
      body: 'After save, copy the webhook URL shown on this page into Meta → WhatsApp → Configuration → Webhook.',
    },
    {
      title: 'Mark products for WhatsApp',
      body: 'In Items / Channel pricing, enable products for WhatsApp, then sync catalogue from this screen.',
    },
  ],
  tips: [
    'Marketing WhatsApp (Settings → Marketing) uses different credentials for outbound messages.',
    'Catalogue sync can take a few minutes after the first push.',
  ],
};

/** UK marketplace ID for SP-API. */
export const AMAZON_UK_MARKETPLACE_ID = 'A1F83G8C2ARO7P';

export const AMAZON_SETUP_GUIDE: SetupGuide = {
  title: 'How to get credentials from Amazon',
  intro:
    'Collect Login with Amazon (LWA) Client ID, Client Secret and Refresh Token from Seller Central / Solution Provider Portal, then paste them into SaleDesk.',
  pathHint:
    'Seller Central → Apps and Services → Develop Apps  ·  UK marketplace ID A1F83G8C2ARO7P',
  steps: [
    {
      title: 'Professional Seller Central account',
      body: 'You need an Amazon Professional selling account (Individual plans cannot use SP-API). Sign in as the Primary User.',
    },
    {
      title: 'Register as a private developer',
      body: 'Open Solution Provider Portal (or Seller Central → Apps and Services → Develop Apps). Complete the developer profile and request roles such as Inventory and Order Listing / Orders.',
    },
    {
      title: 'Create a private SP-API application',
      body: 'Choose Add new app client / Create app. Select Selling Partner API, private application, and the roles Amazon approved on your profile.',
      bullets: ['App name: SaleDesk POS', 'API type: Selling Partner API', 'Application type: Private'],
    },
    {
      title: 'Copy LWA Client ID & Client Secret',
      body: 'On Developer Central, open your app → View under LWA credentials. Copy Client identifier and Client secret.',
    },
    {
      title: 'Authorise the app → Refresh Token',
      body: 'Open Authorize app (self-authorization) for your Seller Central account. Copy the Refresh Token (starts with Atzr|…). Paste into SaleDesk with Client ID / Secret.',
      note: 'Never share Client Secret or Refresh Token in chat. Paste only into SaleDesk.',
    },
    {
      title: 'Marketplace ID & Seller ID',
      body: 'For Amazon UK use marketplace ID A1F83G8C2ARO7P. Seller ID is shown in Seller Central → Settings → Account Info (Merchant Token / Seller ID).',
    },
  ],
  tips: [
    'Keep SaleDesk SKUs aligned with Amazon seller SKUs.',
    'Use Test connection after Save to verify LWA before listing sync.',
    'Official docs: developer-docs.amazon.com/sp-api',
  ],
  warning: 'Never share LWA Client Secret or Refresh Tokens outside SaleDesk.',
};

/** OnBuy UK marketplace site id. */
export const ONBUY_UK_SITE_ID = '2000';

export const ONBUY_SETUP_GUIDE: SetupGuide = {
  title: 'How to get credentials from OnBuy',
  intro:
    'Collect Consumer Key, Secret Key, Seller ID, Seller Entity ID and Site ID from the OnBuy Seller Control Panel — then paste them into SaleDesk.',
  pathHint:
    'Seller Control Panel → Listings → Imports & Integrations → OnBuy API',
  steps: [
    {
      title: 'Sign in to Seller Control Panel',
      body: 'Log in to your OnBuy seller account (seller.onbuy.com or the Control Panel link OnBuy provides).',
    },
    {
      title: 'Open OnBuy API',
      body: 'Go to Listings → Imports & Integrations (or Inventory → Integrations). Click OnBuy API. Enable the integration if prompted — contact OnBuy support if the API page is missing.',
    },
    {
      title: 'Copy connection details',
      body: 'From Connection Details / Enabled Integrations copy:',
      bullets: [
        'Consumer Key (ck_live_… or ck_test_…)',
        'Secret Key (sk_live_… or sk_test_…)',
        'Seller ID',
        'Seller Entity ID',
        'Site ID (UK = 2000)',
      ],
      note: 'Match Live keys with Live mode (and Test with Test). Mixing modes causes “Invalid credentials”.',
    },
    {
      title: 'Paste into SaleDesk',
      body: 'Open Marketplaces → OnBuy → Configure, paste the values, Save, then Test connection (requests an access token from api.onbuy.com).',
    },
  ],
  tips: [
    'UK Site ID is 2000. Other countries use different Site IDs from the API page.',
    'Access tokens from OnBuy expire quickly (~15 minutes) — SaleDesk refreshes them when needed.',
    'Docs: docs.api.onbuy.com',
  ],
  warning: 'Never share Secret Key outside SaleDesk.',
};

/** Common Commerce API scopes for Meta Shops system-user tokens. */
export const META_SHOPS_SCOPES = [
  'catalog_management',
  'business_management',
  'commerce_account_read_settings',
  'commerce_account_manage_orders',
  'commerce_account_read_orders',
  'pages_read_engagement',
];

export const META_SHOPS_SETUP_GUIDE: SetupGuide = {
  title: 'How to get credentials from Meta',
  intro:
    'Collect App ID, system-user Access Token, Catalog ID and Commerce Merchant Settings (CMS) ID from Meta for Developers and Business Suite / Commerce Manager — then paste them into SaleDesk.',
  pathHint:
    'developers.facebook.com → My Apps  ·  Meta Business Suite → System users  ·  Commerce Manager → Catalog',
  steps: [
    {
      title: 'Create a Meta developer app',
      body: 'Go to developers.facebook.com → My Apps → Create App (Business type). Add the app to your Meta Business Suite.',
    },
    {
      title: 'Set up Commerce Manager shop',
      body: 'In Commerce Manager, create or open your Facebook / Instagram Shop, complete commerce checks, and note the Catalog and Commerce account.',
    },
    {
      title: 'Create a system user',
      body: 'In Meta Business Suite → Users → System users, create (or reuse) a system user. Assign admin access to the app, Page, catalog and commerce account.',
    },
    {
      title: 'Generate Access Token',
      body: 'On the system user, Generate Token for your app. Request catalog_management, business_management and commerce order/settings scopes.',
      note: 'Prefer an expiring token (60 days) and rotate it. Never paste tokens into chat.',
    },
    {
      title: 'Copy Catalog ID & CMS ID',
      body: 'In Commerce Manager open the catalog — the Catalog ID is in the URL or settings. Commerce Merchant Settings (CMS) ID is on the shop / commerce account.',
      bullets: ['Catalog ID → Catalog ID field', 'CMS / Commerce account ID → Commerce Account ID', 'Page ID from the Facebook Page'],
    },
    {
      title: 'App ID & App Secret',
      body: 'In Meta for Developers → your app → Settings → Basic, copy App ID and App Secret into SaleDesk.',
    },
  ],
  tips: [
    'WhatsApp Shop under Marketplaces is a separate connector — do not reuse WhatsApp Shop tokens here unless you intentionally share a catalog.',
    'Use Test connection after Save to verify the token can read your catalog.',
    'Docs: developers.facebook.com/docs/commerce-platform/api-setup',
  ],
  warning: 'Never share App Secret or Access Tokens outside SaleDesk.',
};

export const TIKTOK_SHOP_SETUP_GUIDE: SetupGuide = {
  title: 'How to get credentials from TikTok Shop',
  intro:
    'Collect App Key, App Secret, Access Token and Refresh Token from TikTok Shop Partner Center after seller authorization — then paste them into SaleDesk.',
  pathHint:
    'partner.tiktokshop.com → App & Service → Create app  ·  Seller authorize → token exchange',
  steps: [
    {
      title: 'Open Partner Center (ROW / UK)',
      body: 'Sign in at partner.tiktokshop.com (use partner.us.tiktokshop.com only for US). UK sellers use the ROW Partner Center. Complete developer onboarding / compliance if prompted.',
    },
    {
      title: 'Create an app',
      body: 'App & Service → Create app & service. Choose Custom (your shop) or Public. Select Market (UK) and Seller type (Local). Turn on Enable API.',
      bullets: [
        'App name: SaleDesk POS',
        'Market: UK',
        'Enable API: on',
        'Redirect URL: your SaleDesk HTTPS callback (or Support-provided URL)',
      ],
    },
    {
      title: 'Copy App Key & App Secret',
      body: 'Open the app → Credentials. Copy App Key and reveal App Secret (Manage app secret). Service Id appears next to the app name.',
      note: 'A newly rotated secret can take up to ~10 minutes to activate.',
    },
    {
      title: 'Select API scopes',
      body: 'In the app, enable product, inventory and order scopes your integration needs. Sellers will see these scopes when authorising.',
    },
    {
      title: 'Seller authorisation → tokens',
      body: 'Have the shop authorise the app (services.tiktokshop.com/open/authorize). Exchange the one-time auth code for access_token and refresh_token via auth.tiktok-shops.com/api/v2/token/get.',
      note: 'Never share App Secret or tokens in chat. Paste only into SaleDesk.',
    },
    {
      title: 'Shop ID & Shop Cipher',
      body: 'After authorization, copy Shop ID and shop_cipher from the authorized shops response — many OpenAPI calls require shop_cipher.',
    },
  ],
  tips: [
    'Keep SaleDesk SKUs aligned with TikTok Shop seller SKUs.',
    'Use Test connection after Save (refresh-token exchange verifies App Key/Secret).',
    'Docs: partner.tiktokshop.com/docv2',
  ],
  warning: 'Never share App Secret, Access Token or Refresh Token outside SaleDesk.',
};

export const ORDER_NOTIFY_SETUP_GUIDE: SetupGuide = {
  title: 'Setup guide — Order notifications',
  intro:
    'Tell customers when their order is placed, accepted, ready, out for delivery, or completed — on WhatsApp, SMS and/or Email.',
  pathHint: 'Settings → Marketing → Order notifications',
  steps: [
    {
      title: 'Unlock at least one Marketing channel',
      body: 'WhatsApp, SMS or Email add-ons must be enabled. Contact Support if cards show Add-on required.',
    },
    {
      title: 'Configure credentials',
      body: 'Open each channel card → Configure, paste provider credentials, Send test until status is Connected.',
    },
    {
      title: 'Enable channels here',
      body: 'On this page, Enable every connected channel you want to use for status updates.',
    },
    {
      title: 'Choose which events to send',
      body: 'Tick Order placed, Accepted, Ready, etc. Untick any status you do not want messaged.',
    },
    {
      title: 'Review templates',
      body: 'Use Professional default or Custom. Insert merge fields (name, totals, tracking link, items).',
    },
    {
      title: 'Save notifications',
      body: 'Storefront order status changes will send on enabled channels. Walk-in / missing phone or email are skipped.',
    },
  ],
  tips: [
    'Tracking link opens the public storefront order page (no login).',
    'Check Message usage to see how many messages you have consumed.',
  ],
};

export function getMarketplaceScaffoldGuide(channel: string): SetupGuide | null {
  const map: Record<string, SetupGuide> = {
    amazon: AMAZON_SETUP_GUIDE,
    onbuy: ONBUY_SETUP_GUIDE,
    meta: META_SHOPS_SETUP_GUIDE,
    tiktok: TIKTOK_SHOP_SETUP_GUIDE,
    ebay: EBAY_SETUP_GUIDE,
  };
  return map[channel] || null;
}
